import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { divIcon, type LatLngExpression } from 'leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import axiosInstance from '../../initializers/axios';
import '../../../pages/mainLanding/Landing/AddPost.css';
import './LocationPickerModal.css';

export type LocationPickerValue = {
  id: string;
  label: string;
  meta?: string;
  locationId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  type?: string;
};

type Props = {
  open: boolean;
  title?: string;
  initialQuery?: string;
  onClose: () => void;
  onSelect: (value: LocationPickerValue) => void;
};

type PickerMapLayer = 'streets' | 'terrain' | 'satellite';

const LOCATION_ENDPOINT = '/api/feed/locations';
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_CENTER: [number, number] = [41.3231, 19.4414];
const MAP_LAYERS: Record<PickerMapLayer, { label: string; url: string; attribution: string; maxNativeZoom: number }> = {
  streets: {
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxNativeZoom: 19,
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data &copy; OpenStreetMap contributors, SRTM | Map style &copy; OpenTopoMap',
    maxNativeZoom: 17,
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxNativeZoom: 19,
  },
};

const pickerMapPin = divIcon({
  className: 'composer-map-pin-shell',
  html: '<span class="composer-map-pin"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const MapViewportSync: React.FC<{ center: LatLngExpression }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);

  return null;
};

const MapClickCapture: React.FC<{ onPick: (latitude: number, longitude: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click: (event) => {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
};

const LocationPickerModal: React.FC<Props> = ({ open, title, initialQuery = '', onClose, onSelect }) => {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState(initialQuery);
  const [selectedLocation, setSelectedLocation] = useState<LocationPickerValue | null>(null);
  const [savedLocations, setSavedLocations] = useState<LocationPickerValue[]>([]);
  const [remoteLocations, setRemoteLocations] = useState<LocationPickerValue[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeMapLayer, setActiveMapLayer] = useState<PickerMapLayer>('streets');
  const activeLayerConfig = MAP_LAYERS[activeMapLayer];

  useEffect(() => {
    if (!open) return;
    setSearch(initialQuery);
    setSelectedLocation(null);

    void axiosInstance
      .get<Array<{ id: number; name: string; type?: string; lat?: number | null; lng?: number | null }>>(LOCATION_ENDPOINT, { params: { take: 40 } })
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        setSavedLocations(
          items.map((location) => ({
            id: `location-${location.id}`,
            label: location.name,
            meta: location.type || '',
            locationId: location.id,
            latitude: location.lat ?? null,
            longitude: location.lng ?? null,
            type: location.type || 'Place',
          })),
        );
      })
      .catch(() => setSavedLocations([]));
  }, [initialQuery, open]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    const term = search.trim();
    const timer = window.setTimeout(async () => {
      if (term.length < 3) {
        setRemoteLocations([]);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        const query = new URLSearchParams({
          format: 'jsonv2',
          q: term,
          limit: '6',
        });

        const response = await fetch(`${NOMINATIM_SEARCH_URL}?${query.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        const payload = (await response.json()) as Array<{ place_id: number; display_name: string; lat: string; lon: string; type?: string }>;
        setRemoteLocations(
          (payload ?? []).map((item) => {
            const [label, ...rest] = item.display_name.split(',');
            return {
              id: `remote-${item.place_id}`,
              label: label?.trim() || item.display_name,
              meta: rest.join(',').trim(),
              latitude: Number(item.lat),
              longitude: Number(item.lon),
              type: item.type || 'Place',
            };
          }),
        );
      } catch {
        if (!controller.signal.aborted) setRemoteLocations([]);
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 320);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [open, search]);

  const results = useMemo(() => {
    const localMatches = savedLocations.filter((item) => {
      const term = search.trim().toLowerCase();
      if (!term) return true;
      return `${item.label} ${item.meta ?? ''}`.toLowerCase().includes(term);
    });

    const merged = [...localMatches, ...remoteLocations];
    const seen = new Set<string>();
    return merged.filter((item) => {
      const key = `${item.label.trim().toLowerCase()}::${Math.round((item.latitude ?? 0) * 1000)}::${Math.round((item.longitude ?? 0) * 1000)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [remoteLocations, savedLocations, search]);

  const mapCenter = useMemo<LatLngExpression>(() => {
    if (typeof selectedLocation?.latitude === 'number' && typeof selectedLocation?.longitude === 'number') {
      return [selectedLocation.latitude, selectedLocation.longitude];
    }

    const first = results.find((item) => typeof item.latitude === 'number' && typeof item.longitude === 'number');
    if (first && typeof first.latitude === 'number' && typeof first.longitude === 'number') {
      return [first.latitude, first.longitude];
    }

    return DEFAULT_CENTER;
  }, [results, selectedLocation]);

  const handleMapPick = (latitude: number, longitude: number) => {
    setSelectedLocation({
      id: `draft-${latitude}-${longitude}`,
      label: search.trim() || i18n.t('mainLanding.addPost.pinnedLocation', { defaultValue: 'Pinned location' }),
      meta: i18n.t('mainLanding.addPost.customMapPoint', { defaultValue: 'Custom map point' }),
      latitude,
      longitude,
      type: 'Place',
    });
  };

  const persistLocation = async (location: LocationPickerValue): Promise<LocationPickerValue> => {
    if (typeof location.locationId === 'number') return location;
    if (typeof location.latitude !== 'number' || typeof location.longitude !== 'number') return location;

    const response = await axiosInstance.post(LOCATION_ENDPOINT, {
      name: location.label,
      type: location.type || 'Place',
      lat: location.latitude,
      lng: location.longitude,
    });

    const saved = response.data as { id: number; name: string; type?: string; lat?: number | null; lng?: number | null };
    return {
      ...location,
      id: `location-${saved.id}`,
      label: saved.name,
      meta: saved.type || location.meta,
      locationId: saved.id,
      latitude: saved.lat ?? location.latitude ?? null,
      longitude: saved.lng ?? location.longitude ?? null,
      type: saved.type || location.type,
    };
  };

  const handleUseLocation = async () => {
    if (!selectedLocation) return;
    const persisted = await persistLocation(selectedLocation);
    onSelect(persisted);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="composer-modal-backdrop composer-modal-backdrop--solid" onClick={onClose}>
      <div className="composer-modal composer-modal--location composer-modal--solid location-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="composer-modal-header">
          <h4>{title || i18n.t('mainLanding.addPost.pickLocation', { defaultValue: 'Choose location' })}</h4>
          <button type="button" className="composer-modal-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <input
          type="text"
          className="location-search-input"
          placeholder={i18n.t('mainLanding.addPost.searchLocation', { defaultValue: 'Search location' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="location-map-preview">
          <LocationOnIcon fontSize="small" />
          <span>{selectedLocation?.label ?? i18n.t('mainLanding.addPost.selectMapPoint', { defaultValue: 'Choose a point on the map' })}</span>
        </div>

        <p className="location-map-hint">{i18n.t('mainLanding.addPost.mapHint', { defaultValue: 'Search for a place or click on the map to drop a marker.' })}</p>

        <div className="location-map-layer-switcher" role="tablist" aria-label={i18n.t('mainLanding.map.styleLabel', { defaultValue: 'Map style' })}>
          {(Object.entries(MAP_LAYERS) as Array<[PickerMapLayer, (typeof MAP_LAYERS)[PickerMapLayer]]>).map(([layerKey, layer]) => (
            <button
              key={layerKey}
              type="button"
              className={`location-map-layer-btn ${activeMapLayer === layerKey ? 'is-active' : ''}`}
              onClick={() => setActiveMapLayer(layerKey)}
            >
              {i18n.t(`mainLanding.map.layers.${layerKey}`, { defaultValue: layer.label })}
            </button>
          ))}
        </div>

        <div className="location-map-canvas">
          <MapContainer center={mapCenter} zoom={13} minZoom={3} maxZoom={activeLayerConfig.maxNativeZoom} scrollWheelZoom className="location-leaflet-map">
            <TileLayer
              key={activeMapLayer}
              attribution={activeLayerConfig.attribution}
              url={activeLayerConfig.url}
              maxZoom={activeLayerConfig.maxNativeZoom}
              maxNativeZoom={activeLayerConfig.maxNativeZoom}
            />
            <MapViewportSync center={mapCenter} />
            <MapClickCapture onPick={handleMapPick} />
            {results
              .filter((item) => typeof item.latitude === 'number' && typeof item.longitude === 'number')
              .map((item) => (
                <Marker
                  key={item.id}
                  position={[item.latitude as number, item.longitude as number]}
                  icon={pickerMapPin}
                  eventHandlers={{ click: () => setSelectedLocation(item) }}
                />
              ))}
            {selectedLocation && typeof selectedLocation.latitude === 'number' && typeof selectedLocation.longitude === 'number' ? (
              <Marker position={[selectedLocation.latitude, selectedLocation.longitude]} icon={pickerMapPin} />
            ) : null}
          </MapContainer>
        </div>

        <div className="location-results">
          {isSearching ? <p className="composer-modal-empty">{i18n.t('mainLanding.addPost.searchingLocations', { defaultValue: 'Searching places...' })}</p> : null}
          {!isSearching && results.length === 0 ? <p className="composer-modal-empty">{i18n.t('mainLanding.addPost.noLocationsFound', { defaultValue: 'No locations found yet.' })}</p> : null}
          {results.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`location-result-item ${selectedLocation?.id === item.id ? 'active' : ''}`}
              onClick={() => setSelectedLocation(item)}
            >
              <strong>{item.label}</strong>
              {item.meta ? <span>{item.meta}</span> : null}
            </button>
          ))}
        </div>

        <div className="composer-modal-actions">
          <button type="button" className="cancel-btn-modern" onClick={onClose}>
            {i18n.t('mainLanding.actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button type="button" className={`submit-btn-modern ${selectedLocation ? 'active' : ''}`} disabled={!selectedLocation} onClick={handleUseLocation}>
            {i18n.t('mainLanding.addPost.useLocation', { defaultValue: 'Use location' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
